import { Button, Spinner } from "@chakra-ui/react";
import React from "react";
import AppModal from "components/modal/AppModal";

/**
 * Reenvío de un documento ya emitido al correo del cliente.
 *
 * El destinatario viene precargado con el que ya tiene el documento: el caso
 * normal es "mándasela de nuevo", no "escribe una dirección". Se puede editar
 * cuando la original estaba mala.
 *
 * No emite, no anula y no consume folios: vuelve a mandar el mismo PDF y el
 * mismo XML firmado que ya existen.
 */

const RE_EMAIL = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

function listaValida(valor) {
  const partes = String(valor || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (!partes.length) return { ok: true, vacia: true };
  return { ok: partes.every((p) => RE_EMAIL.test(p)), vacia: false };
}

export default function ReenviarCorreoModal({
  isOpen,
  onClose,
  documento,
  enviando = false,
  onConfirmar,
}) {
  const [para, setPara] = React.useState("");
  const [cc, setCc] = React.useState("");
  const [tocado, setTocado] = React.useState(false);

  // Al abrir para otro documento se recarga el destinatario guardado. Arrastrar
  // el correo escrito para otra factura la mandaría al cliente equivocado.
  React.useEffect(() => {
    if (isOpen) {
      setPara(String(documento?.delivery_to || "").trim());
      setCc(String(documento?.delivery_cc || "").trim());
      setTocado(false);
    }
  }, [isOpen, documento?.id, documento?.delivery_to, documento?.delivery_cc]);

  const paraLimpio = para.trim();
  const paraValido = RE_EMAIL.test(paraLimpio);
  const ccEstado = listaValida(cc);
  const puedeEnviar = paraValido && ccEstado.ok && !enviando;

  const confirmar = () => {
    setTocado(true);
    if (!puedeEnviar) return;
    onConfirmar({
      to: paraLimpio,
      cc: ccEstado.vacia ? undefined : cc.trim(),
    });
  };

  const referencia = documento
    ? [
        documento.receptor_razon_social,
        documento.folio ? `Folio ${documento.folio}` : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const sinCorreoGuardado = isOpen && !String(documento?.delivery_to || "").trim();

  return (
    <AppModal
      isOpen={isOpen}
      onClose={enviando ? () => {} : onClose}
      size="md"
      closeOnOverlayClick={!enviando}
      title="Reenviar al cliente"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose} isDisabled={enviando}>
            Cancelar
          </Button>
          <Button size="sm" colorScheme="brand" onClick={confirmar} isDisabled={!puedeEnviar}>
            {enviando ? (
              <span className="flex items-center gap-2">
                <Spinner size="xs" />
                Enviando…
              </span>
            ) : (
              "Enviar"
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {referencia ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">{referencia}</p>
        ) : null}

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
            Para
          </span>
          <input
            type="email"
            value={para}
            onChange={(e) => setPara(e.target.value)}
            onBlur={() => setTocado(true)}
            disabled={enviando}
            placeholder="cliente@empresa.cl"
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 disabled:opacity-60 dark:border-white/15 dark:bg-navy-800 dark:text-white"
          />
          {tocado && !paraValido ? (
            <span className="text-xs text-red-600 dark:text-red-300">
              Escribe un correo válido.
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
            Con copia (opcional)
          </span>
          <input
            type="text"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            disabled={enviando}
            placeholder="contabilidad@empresa.cl, bodega@empresa.cl"
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 disabled:opacity-60 dark:border-white/15 dark:bg-navy-800 dark:text-white"
          />
          {!ccEstado.ok ? (
            <span className="text-xs text-red-600 dark:text-red-300">
              Separa los correos con coma y revisa que estén bien escritos.
            </span>
          ) : null}
        </label>

        {sinCorreoGuardado ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900 dark:bg-amber-400/10 dark:text-amber-200">
            Este documento no tiene un correo guardado. El que escribas queda
            registrado como destinatario.
          </p>
        ) : null}

        <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600 dark:bg-white/5 dark:text-gray-300">
          Se envía el PDF y el XML firmado. No se emite nada nuevo ni se gasta un
          folio: es el mismo documento que ya existe.
        </p>
      </div>
    </AppModal>
  );
}
